import React, { useState, useEffect } from 'react';
import apiClient from '../apiClient'; // Import the new apiClient
import { FaPlus, FaClipboardList, FaTrash, FaExclamationTriangle, FaLightbulb, FaSmile, FaUsers, FaRocket, FaUserPlus } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ParticipationTrackingModal from '../components/assessment/ParticipationTrackingModal';
import AssignStakeholdersModal from '../components/assessment/AssignStakeholdersModal';
import DeployAssessmentModal from '../components/assessment/DeployAssessmentModal';
import ConfirmationModal from '../components/DeleteConfirmationModal';
import { HiSparkles } from 'react-icons/hi2';
import './Assessments.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function Assessments() {
  const [assessments, setAssessments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [assessmentTemplates, setAssessmentTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [addAssessmentError, setAddAssessmentError] = useState(null);

  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showParticipationModal, setShowParticipationModal] = useState(false);
  const [addingAssessment, setAddingAssessment] = useState(false);
  const [participationAssessmentId, setParticipationAssessmentId] = useState(null);
  const [participationAssessmentType, setParticipationAssessmentType] = useState('');

  // Assign Stakeholders Modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignAssessmentId, setAssignAssessmentId] = useState(null);
  const [assignProjectId, setAssignProjectId] = useState(null);

  // Deploy Assessment Modal state
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [deployAssessmentId, setDeployAssessmentId] = useState(null);
  const [deployAssessmentType, setDeployAssessmentType] = useState('');

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: 'Cancel',
    type: 'danger',
    onConfirm: null,
    isLoading: false
  });

  // Helper functions for confirmation modal
  const openConfirmationModal = ({ title, message, confirmText, type = 'danger', onConfirm }) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      confirmText,
      cancelText: 'Cancel',
      type,
      onConfirm,
      isLoading: false
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
  };

  const setConfirmationLoading = (isLoading) => {
    setConfirmationModal(prev => ({ ...prev, isLoading }));
  };

  const fetchProjects = () => {
    setLoadingProjects(true);
    apiClient.get('/projects/for-assessment') // Use optimized endpoint for assessment creation
      .then(response => {
        setProjects(response.data);
        setLoadingProjects(false);
      })
      .catch(error => {
        console.error('Error fetching projects:', error);
        const errorMsg = error.response?.data?.error || error.message || "An unknown error occurred";
        setError(`Failed to load projects: ${errorMsg}. Cannot select assessments.`);
        setLoadingProjects(false);
      });
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    setLoadingTemplates(true);
    apiClient.get('/assessment-templates')
      .then(res => {
        setAssessmentTemplates(res.data.templates || []);
        setLoadingTemplates(false);
      })
      .catch(err => {
        setAssessmentTemplates([]);
        setLoadingTemplates(false);
      });
  }, []);

  const fetchAssessments = (projectId) => {
    if (!projectId) {
      setAssessments([]);
      setLoadingAssessments(false);
      return;
    }
    setLoadingAssessments(true);
    setError(null);
    apiClient.get(`/assessments?project_id=${projectId}`) // Use apiClient, remove /api
      .then(response => {
        setAssessments(response.data);
        setLoadingAssessments(false);
      })
      .catch(error => {
        console.error('Error fetching assessments:', error);
        const errorMsg = error.response?.data?.error || error.message || "An unknown error occurred";
        setError(`Failed to load assessments: ${errorMsg}. Is the backend running?`);
        setLoadingAssessments(false);
      });
  };

  useEffect(() => {
    fetchAssessments(selectedProjectId);
  }, [selectedProjectId]);

  const handleProjectChange = (event) => {
    setSelectedProjectId(event.target.value);
  };

  const selectedProject = projects.find(p => p.id === parseInt(selectedProjectId));

  const openAddModal = () => {
    setSelectedTemplateId('');
    setAddAssessmentError(null);
    setSuccessMessage(null);
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
  };

  const handleAddAssessment = () => {
    if (!selectedTemplateId) {
      setAddAssessmentError('Please select an assessment template.');
      return;
    }
    setAddAssessmentError(null);
    setAddingAssessment(true);
    const selectedTemplate = assessmentTemplates.find(t => t.id === selectedTemplateId);
    if (!selectedTemplate) {
        setAddAssessmentError('Invalid template selected.');
        return;
    }
    const newAssessmentData = {
      project_id: parseInt(selectedProjectId),
      assessment_type: selectedTemplate.name
    };
    apiClient.post('/assessments/', newAssessmentData) // Use apiClient, remove /api
      .then(response => {
        setSuccessMessage(response.data.message || 'Assessment added successfully!');
        fetchAssessments(selectedProjectId);
        fetchProjects(); // Refresh project counts
        closeAddModal();
        setTimeout(() => setSuccessMessage(null), 3000);
      })
      .catch(err => {
        console.error('Error adding assessment:', err);
        const errorMsg = err.response?.data?.error || err.message || 'Failed to add assessment. Please try again.';
        setAddAssessmentError(errorMsg);
      })
      .finally(() => {
        setAddingAssessment(false);
      });
  };

  const openDeployModal = (assessmentId, assessmentType) => {
    setDeployAssessmentId(assessmentId);
    setDeployAssessmentType(assessmentType);
    setShowDeployModal(true);
  };

  const closeDeployModal = () => {
    setShowDeployModal(false);
    setDeployAssessmentId(null);
    setDeployAssessmentType('');
  };

  const handleDeployAssessment = async (deployAt = null) => {
    setError(null);
    setSuccessMessage(null);
    try {
      const payload = deployAt ? { deploy_at: deployAt } : {};
      const response = await apiClient.post(`/assessments/${deployAssessmentId}/deploy`, payload);
      setSuccessMessage(response.data.message || 'Assessment deployed successfully.');
      fetchAssessments(selectedProjectId);
      setTimeout(() => setSuccessMessage(null), 5000);
      closeDeployModal();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to deploy assessment.';
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
      throw err; // Re-throw so modal can handle the error
    }
  };

  const handleDeleteAssessment = (assessmentId, assessmentType) => {
    openConfirmationModal({
      title: 'Delete Assessment',
      message: `Are you sure you want to delete the "${assessmentType}" assessment (ID: ${assessmentId})? This action cannot be undone.`,
      confirmText: 'Delete Assessment',
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          setError(null);
          setSuccessMessage(null);
          const response = await apiClient.delete(`/assessments/${assessmentId}`);
          setSuccessMessage(response.data.message || `Assessment ${assessmentId} deleted successfully.`);
          fetchAssessments(selectedProjectId);
          fetchProjects(); // Refresh project counts
          setTimeout(() => setSuccessMessage(null), 3000);
          closeConfirmationModal();
        } catch (err) {
          console.error(`Error deleting assessment ${assessmentId}:`, err);
          const errorMsg = err.response?.data?.error || err.message || 'Failed to delete assessment.';
          setError(errorMsg);
          setTimeout(() => setError(null), 5000);
          closeConfirmationModal();
        }
      }
    });
  };



  const openParticipationModal = (assessmentId, assessmentType) => {
    setParticipationAssessmentId(assessmentId);
    setParticipationAssessmentType(assessmentType);
    setShowParticipationModal(true);
  };

  const closeParticipationModal = () => {
    setShowParticipationModal(false);
    setParticipationAssessmentId(null);
    setParticipationAssessmentType('');
  };

  const openAssignModal = (assessmentId) => {
    setAssignAssessmentId(assessmentId);
    setAssignProjectId(parseInt(selectedProjectId));
    setShowAssignModal(true);
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setAssignAssessmentId(null);
    setAssignProjectId(null);
  };

  const handleAssignmentChange = () => {
    // Refresh assessments list when stakeholder assignments change
    fetchAssessments(selectedProjectId);
    fetchProjects(); // Refresh project counts
  };

  const handleAnalyze = async (assessmentId) => {
    setAnalyzingId(assessmentId);
    setAnalysisResult(null);
    setShowAnalysisModal(true);
    try {
      const res = await apiClient.post(`/assessments/${assessmentId}/analyze`);
      setAnalysisResult(res.data.analysis);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to analyze.';
      if (errorMessage.includes('No responses found')) {
        setAnalysisResult('No one has taken this assessment yet.');
      } else {
        setAnalysisResult(errorMessage);
      }
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleProjectAIAnalyze = async () => {
    setAnalyzingId('project');
    setAnalysisResult(null);
    setShowAnalysisModal(true);
    try {
      // Only send project_id, let backend select completed assessments
      const res = await apiClient.post(`/assessments/analyze-project`, {
        project_id: selectedProjectId
      });
      setAnalysisResult(res.data.analysis);
    } catch (err) {
      setAnalysisResult(err.response?.data?.error || err.message || 'Failed to analyze project assessments.');
    } finally {
      setAnalyzingId(null);
    }
  };

  // Helper to render the AI analysis result in a modern, actionable way
  const renderAnalysisResult = (result) => {
    let parsed;
    try {
      parsed = typeof result === 'string' ? JSON.parse(result) : result;
      if (parsed && parsed.error) {
        return <div style={{ color: 'red', fontWeight: 600, padding: '1.5rem' }}>{parsed.error}</div>;
      }
    } catch (e) {
      // Handle the "no one has taken assessment" case with a friendly message
      if (typeof result === 'string' && result.toLowerCase().includes('no one has taken this assessment yet')) {
        return (
          <div style={{ 
            padding: '2rem', 
            textAlign: 'center',
            background: '#f8fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <h4 style={{ color: '#1e293b', margin: '0 0 1rem 0' }}>No Assessment Data Yet</h4>
            <p style={{ color: '#64748b', margin: '0 0 1.5rem 0', lineHeight: '1.6' }}>
              This assessment hasn't been completed by anyone yet. Once stakeholders start taking the assessment, 
              I'll be able to provide insights and analysis!
            </p>
            <div style={{ 
              background: '#e0f2fe', 
              border: '1px solid #0ea5e9', 
              borderRadius: '8px', 
              padding: '1rem',
              color: '#0c4a6e'
            }}>
              <strong>💡 Tip:</strong> Use the 👥 Users button to see who's assigned and track participation.
            </div>
          </div>
        );
      }
      
      // Handle other error cases more gracefully
      if (typeof result === 'string' && (result.toLowerCase().includes('no completed assessments') || result.toLowerCase().includes('error'))) {
        return (
          <div style={{ 
            padding: '1.5rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#991b1b'
          }}>
            <strong>⚠️ Analysis Unavailable</strong>
            <p style={{ margin: '0.5rem 0 0 0' }}>{result}</p>
          </div>
        );
      }
      
      // Fallback for unexpected errors
      return (
        <div style={{ 
          padding: '1.5rem',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b'
        }}>
          <strong>🤖 Analysis Error</strong>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            I encountered an unexpected issue while analyzing the assessment data. Please try again or contact support if the problem persists.
          </p>
        </div>
      );
    }
    return (
      <div className="ai-analysis-result" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: 8 }}>
        <div style={{ background: '#f0f7ff', borderRadius: 12, boxShadow: '0 2px 8px #e0e7ef', padding: '1.2rem 1.5rem', borderLeft: '6px solid #2563eb', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <HiSparkles style={{ color: '#2563eb', fontSize: '2em', flexShrink: 0 }} />
          <div>
            <h4 style={{ margin: 0, color: '#2563eb', fontWeight: 700 }}>Summary of Insights</h4>
            <div style={{ color: '#222', marginTop: 6 }}>{parsed.summary_of_insights || <span className="text-gray-400">No summary provided.</span>}</div>
          </div>
        </div>
        <div style={{ background: '#fff7e6', borderRadius: 12, boxShadow: '0 2px 8px #fbeedb', padding: '1.2rem 1.5rem', borderLeft: '6px solid #eab308', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <FaExclamationTriangle style={{ color: '#eab308', fontSize: '2em', flexShrink: 0 }} />
          <div>
            <h4 style={{ margin: 0, color: '#eab308', fontWeight: 700 }}>Risk Areas & Gaps</h4>
            <div style={{ color: '#222', marginTop: 6 }}>{parsed.risk_areas_and_gaps || <span className="text-gray-400">No risk areas provided.</span>}</div>
          </div>
        </div>
        <div style={{ background: '#e6f7f7', borderRadius: 12, boxShadow: '0 2px 8px #d1f0f0', padding: '1.2rem 1.5rem', borderLeft: '6px solid #10b981', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <FaLightbulb style={{ color: '#10b981', fontSize: '2em', flexShrink: 0 }} />
          <div>
            <h4 style={{ margin: 0, color: '#10b981', fontWeight: 700 }}>Recommendations</h4>
            <div style={{ color: '#222', marginTop: 6 }}>{parsed.recommendations || <span className="text-gray-400">No recommendations provided.</span>}</div>
          </div>
        </div>
        <div style={{ background: '#f3e8ff', borderRadius: 12, boxShadow: '0 2px 8px #e9d5ff', padding: '1.2rem 1.5rem', borderLeft: '6px solid #a21caf', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <FaClipboardList style={{ color: '#a21caf', fontSize: '2em', flexShrink: 0 }} />
          <div>
            <h4 style={{ margin: 0, color: '#a21caf', fontWeight: 700 }}>Follow-up Assessments & Plans</h4>
            <div style={{ color: '#222', marginTop: 6 }}>{parsed.followup_assessments_and_plans || <span className="text-gray-400">No follow-up provided.</span>}</div>
          </div>
        </div>
        {parsed.sentiment_analysis && (
          <div style={{ background: '#fffbe7', borderRadius: 12, boxShadow: '0 2px 8px #f7e9b7', padding: '1.2rem 1.5rem', borderLeft: '6px solid #f59e42', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <FaSmile style={{ color: '#f59e42', fontSize: '2em', flexShrink: 0 }} />
            <div>
              <h4 style={{ margin: 0, color: '#f59e42', fontWeight: 700 }}>Sentiment Analysis</h4>
              <div style={{ color: '#222', marginTop: 6 }}>{parsed.sentiment_analysis}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="assessments-page">
      {/* Clean page header like Plans page */}
      <div className="page-header">
        <h2>Assessments</h2>
        <div className="header-actions">
          <select
            className="project-select"
            value={selectedProjectId}
            onChange={handleProjectChange}
            disabled={loadingProjects || projects.length === 0}
          >
            <option value="" disabled>-- Select a Project --</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
                {project.project_priority && ` [${project.project_priority.toUpperCase()}]`}
                {project.change_impact_level && ` - ${project.change_impact_level} impact`}
              </option>
            ))}
          </select>
          {selectedProjectId && (
            <>
              <button
                className="btn-primary"
                onClick={openAddModal}
                disabled={!selectedProjectId}
              >
                <FaPlus /> New Assessment
              </button>
              <button
                className="btn-secondary"
                title="AI Analyze All Completed Assessments"
                onClick={handleProjectAIAnalyze}
                disabled={!selectedProjectId || analyzingId === 'project'}
              >
                <HiSparkles />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="error-message">
          <FaExclamationTriangle /> {error}
        </div>
      )}

      {/* Project Context Display */}
      {selectedProject && (
        <div className="project-context-card">
          <div className="project-context-header">
            <h3>{selectedProject.name}</h3>
            <div className="project-badges">
              {selectedProject.project_priority && (
                <span className={`badge priority-${selectedProject.project_priority?.toLowerCase()}`}>
                  {selectedProject.project_priority}
                </span>
              )}
              {selectedProject.change_impact_level && (
                <span className={`badge impact-${selectedProject.change_impact_level?.toLowerCase()}`}>
                  {selectedProject.change_impact_level} Impact
                </span>
              )}
              {selectedProject.complexity_level && (
                <span className={`badge complexity-${selectedProject.complexity_level?.toLowerCase()}`}>
                  {selectedProject.complexity_level} Complexity
                </span>
              )}
            </div>
          </div>
          {selectedProject.project_objective && (
            <p className="project-objective">
              <strong>Objective:</strong> {selectedProject.project_objective}
            </p>
          )}
          <div className="project-stats">
            <span>📊 {selectedProject.assessment_count} assessments</span>
            <span>👥 {selectedProject.stakeholder_count} stakeholders</span>
            <span>📅 {selectedProject.project_phase}</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!selectedProjectId ? (
        <div className="select-project-message">
          Please select a project from the dropdown above to view or manage its assessments.
        </div>
      ) : loadingAssessments ? (
        <div className="loading-message">
          Loading assessments...
        </div>
      ) : assessments.length === 0 ? (
        <div className="no-assessments">
          No assessments found for this project. Click "New Assessment" to create one.
        </div>
      ) : (
        <div className="assessments-container">
          <div className="assessments-table-container">
            <table className="assessments-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Last Modified</th>
                  <th>Risk</th>
                  <th>Readiness</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assessments.map(assessment => (
                  <tr key={assessment.id}>
                    <td data-label="ID">{assessment.id}</td>
                    <td data-label="Type" className="type-cell">{assessment.assessment_type}</td>
                    <td data-label="Status">
                      <span className={`status-badge ${assessment.status}`}>
                        {assessment.status === 'completed' ? 'Completed' : assessment.status}
                      </span>
                    </td>
                    <td data-label="Last Modified">
                      {assessment.completion_date ? new Date(assessment.completion_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td data-label="Risk">{assessment.risk_level || 'N/A'}</td>
                    <td data-label="Readiness">
                      {assessment.readiness_score !== null ? `${assessment.readiness_score}%` : 'N/A'}
                    </td>
                    <td data-label="Actions" className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          onClick={() => openAssignModal(assessment.id)}
                          disabled={assessment.status === 'Deployed' || assessment.status === 'Completed'}
                          title={
                            assessment.status === 'Deployed' || assessment.status === 'Completed'
                              ? "Cannot assign stakeholders - assessment is already deployed or completed"
                              : "Assign Stakeholders"
                          }
                        >
                          <FaUserPlus />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => openDeployModal(assessment.id, assessment.assessment_type)}
                          disabled={assessment.status === 'Deployed' || assessment.status === 'Completed'}
                          title={
                            assessment.status === 'Deployed' || assessment.status === 'Completed'
                              ? "Assessment is already deployed or completed"
                              : "Deploy Assessment"
                          }
                        >
                          <FaRocket />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => openParticipationModal(assessment.id, assessment.assessment_type)}
                          title="View Participation"
                        >
                          <FaUsers />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleAnalyze(assessment.id)}
                          disabled={analyzingId === assessment.id}
                          title="AI Analysis"
                        >
                          <HiSparkles />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDeleteAssessment(assessment.id, assessment.assessment_type)}
                          title="Delete Assessment"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals remain the same */}
      {isAddModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Assessment to Project: {selectedProject?.name}</h3>
              <button onClick={closeAddModal} className="close-button">×</button>
            </div>
            <div className="modal-body">
              {addAssessmentError && <div className="error-message">{addAssessmentError}</div>}
              <p>Select an assessment template to add:</p>
              {loadingTemplates ? (
                <div>Loading templates...</div>
              ) : (
                <div className="template-card-grid">
                  {assessmentTemplates.length === 0 ? (
                    <div>No templates available.</div>
                  ) : (
                    assessmentTemplates.map(template => (
                      <div 
                        key={template.id} 
                        onClick={() => handleTemplateSelect(template.id)}
                        className={`template-card ${selectedTemplateId === template.id ? 'selected' : ''}`}
                      >
                        <FaClipboardList className="template-icon" />
                        <strong>{template.name}</strong>
                        <p>{template.description}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={closeAddModal}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleAddAssessment}
                disabled={!selectedTemplateId || addingAssessment}
              >
                {addingAssessment ? 'Adding...' : 'Add Assessment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Results Modal */}
      {showAnalysisModal && analysisResult && (
        <div className="modal-overlay">
          <div className="modal-content analysis-modal">
            <div className="modal-header">
              <h3>AI Analysis Results</h3>
              <button onClick={() => setShowAnalysisModal(false)} className="close-button">×</button>
            </div>
            <div className="modal-body">
              {renderAnalysisResult(analysisResult)}
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={() => setShowAnalysisModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Participation Modal */}
      {showParticipationModal && (
        <ParticipationTrackingModal
          isOpen={showParticipationModal}
          onClose={closeParticipationModal}
          assessmentId={participationAssessmentId}
          assessmentType={participationAssessmentType}
        />
      )}

      {/* Assign Stakeholders Modal */}
      {showAssignModal && (
        <AssignStakeholdersModal
          isOpen={showAssignModal}
          onClose={closeAssignModal}
          assessmentId={assignAssessmentId}
          projectId={assignProjectId}
          onAssignmentChange={handleAssignmentChange}
        />
      )}

      {/* Deploy Assessment Modal */}
      {showDeployModal && (
        <DeployAssessmentModal
          isOpen={showDeployModal}
          onClose={closeDeployModal}
          onDeploy={handleDeployAssessment}
          assessmentType={deployAssessmentType}
          isDeployed={false}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        type={confirmationModal.type}
        onConfirm={confirmationModal.onConfirm}
        onClose={closeConfirmationModal}
        isLoading={confirmationModal.isLoading}
      />
    </div>
  );
}

export default Assessments;

